import React, { useEffect, useRef, useState } from 'react';
import { Video, VideoOff, Mic, MicOff, Maximize2, Minimize2, AlertCircle } from 'lucide-react';
import { socket } from '../services/socket';
import { useStore } from '../store/useStore';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

const configuration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    {
      urls: 'turn:numb.viagenie.ca',
      username: 'webrtc@live.com',
      credential: 'muazkh'
    },
    {
      urls: 'turn:openrelay.metered.ca:80',
      username: 'openrelayproject',
      credential: 'openrelayproject'
    }
  ]
};

export const VideoChat: React.FC = () => {
  const { t } = useTranslation();
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const localStream = useRef<MediaStream | null>(null);
  const audioTrackRef = useRef<MediaStreamTrack | null>(null);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);
  const currentPartner = useStore((state) => state.currentPartner);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isConnecting, setIsConnecting] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    const setupWebRTC = async () => {
      try {
        setPermissionDenied(false);
        setConnectionError(null);

        // Create new RTCPeerConnection
        peerConnection.current = new RTCPeerConnection(configuration);

        // Get local media stream
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
        localStream.current = stream;

        // Store audio and video tracks references
        audioTrackRef.current = stream.getAudioTracks()[0];
        videoTrackRef.current = stream.getVideoTracks()[0];

        // Enable audio by default
        if (audioTrackRef.current) {
          audioTrackRef.current.enabled = true;
        }

        // Display local stream
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        // Add tracks to peer connection
        stream.getTracks().forEach(track => {
          if (peerConnection.current) {
            peerConnection.current.addTrack(track, stream);
          }
        });

        // Handle incoming stream
        peerConnection.current.ontrack = (event) => {
          console.log('Received remote stream');
          if (remoteVideoRef.current && event.streams[0]) {
            remoteVideoRef.current.srcObject = event.streams[0];
          }
        };

        // ICE candidate handling
        peerConnection.current.onicecandidate = (event) => {
          if (event.candidate && currentPartner) {
            console.log('Sending ICE candidate');
            socket.emit('ice-candidate', {
              candidate: event.candidate,
              to: currentPartner.socketId
            });
          }
        };

        // Connection state changes
        peerConnection.current.onconnectionstatechange = () => {
          const state = peerConnection.current?.connectionState;
          console.log('Connection state:', state);
          if (state === 'connected') {
            setIsConnecting(false);
            setConnectionError(null);
          } else if (state === 'failed' || state === 'disconnected') {
            setConnectionError(t('video.connection_failed') || 'Conexão falhou. Tente novamente.');
          }
        };

        // ICE connection state changes
        peerConnection.current.oniceconnectionstatechange = () => {
          console.log('ICE connection state:', peerConnection.current?.iceConnectionState);
        };

        // Create and send offer if we have a partner
        if (currentPartner) {
          console.log('Creating offer for:', currentPartner.name);
          const offer = await peerConnection.current.createOffer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: true
          });
          await peerConnection.current.setLocalDescription(offer);
          socket.emit('offer', {
            offer,
            to: currentPartner.socketId
          });
        }
      } catch (error: unknown) {
        console.error('Error setting up WebRTC:', error);
        if (error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')) {
          setPermissionDenied(true);
        } else if (error instanceof Error && error.name === 'NotFoundError') {
          setConnectionError(t('video.no_device') || 'Câmera ou microfone não encontrado.');
        } else {
          setConnectionError(t('video.setup_error') || 'Erro ao configurar vídeo. Verifique suas permissões.');
        }
        setIsConnecting(false);
      }
    };

    setupWebRTC();

    return () => {
      // Cleanup
      if (localStream.current) {
        localStream.current.getTracks().forEach(track => track.stop());
      }
      if (peerConnection.current) {
        peerConnection.current.close();
      }
    };
  }, [currentPartner, t]);

  useEffect(() => {
    // Handle incoming WebRTC signaling
    socket.on('offer', async ({ offer, from }) => {
      console.log('Received offer from:', from);
      try {
        if (!peerConnection.current) return;
        await peerConnection.current.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await peerConnection.current.createAnswer();
        await peerConnection.current.setLocalDescription(answer);
        socket.emit('answer', { answer, to: from });
      } catch (error) {
        console.error('Error handling offer:', error);
      }
    });

    socket.on('answer', async ({ answer }) => {
      console.log('Received answer');
      try {
        if (!peerConnection.current) return;
        await peerConnection.current.setRemoteDescription(new RTCSessionDescription(answer));
      } catch (error) {
        console.error('Error handling answer:', error);
      }
    });

    socket.on('ice-candidate', async ({ candidate }) => {
      console.log('Received ICE candidate');
      try {
        if (!peerConnection.current) return;
        await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (error) {
        console.error('Error adding ICE candidate:', error);
      }
    });

    return () => {
      socket.off('offer');
      socket.off('answer');
      socket.off('ice-candidate');
    };
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      remoteVideoRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const toggleVideo = () => {
    if (videoTrackRef.current) {
      videoTrackRef.current.enabled = !isVideoEnabled;
      setIsVideoEnabled(!isVideoEnabled);
    }
  };

  const toggleAudio = () => {
    if (audioTrackRef.current) {
      audioTrackRef.current.enabled = !isAudioEnabled;
      setIsAudioEnabled(!isAudioEnabled);
    }
  };

  // Permission denied UI
  if (permissionDenied) {
    return (
      <div className="relative w-full aspect-video bg-gray-900 rounded-lg overflow-hidden video-container flex items-center justify-center">
        <div className="text-center p-6">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-white text-xl font-bold mb-2">
            {t('video.permission_denied') || 'Permissão de câmera/microfone negada'}
          </h3>
          <p className="text-gray-400 mb-4">
            {t('video.enable_camera') || 'Habilite a câmera e microfone nas configurações do navegador para usar o vídeo chat.'}
          </p>
          <p className="text-gray-500 text-sm">
            {t('video.permission_help') || 'Clique no ícone de cadeado na barra de endereço para alterar as permissões.'}
          </p>
        </div>
      </div>
    );
  }

  // Connection error UI
  if (connectionError && !isConnecting) {
    return (
      <div className="relative w-full aspect-video bg-gray-900 rounded-lg overflow-hidden video-container flex items-center justify-center">
        <div className="text-center p-6">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-white text-xl font-bold mb-2">
            {t('video.error') || 'Erro de conexão'}
          </h3>
          <p className="text-gray-400">{connectionError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-video bg-gray-900 rounded-lg overflow-hidden video-container">
      {isConnecting && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-yellow-400 border-t-transparent mx-auto mb-4"></div>
            <div className="text-white text-lg">{t('video.connecting') || 'Conectando ao parceiro...'}</div>
          </div>
        </div>
      )}
      <video
        ref={remoteVideoRef}
        autoPlay
        playsInline
        className="w-full h-full object-cover"
      />
      <video
        ref={localVideoRef}
        autoPlay
        playsInline
        muted
        className="absolute bottom-4 right-4 w-1/4 aspect-video object-cover rounded-lg border-2 border-yellow-400 local-video"
      />
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 md:space-x-4 bg-black/50 p-2 rounded-lg">
        <button
          onClick={toggleVideo}
          className={clsx(
            'p-2 rounded-full',
            isVideoEnabled ? 'bg-yellow-400' : 'bg-red-500'
          )}
          title={isVideoEnabled ? t('video.disable_video') || 'Desativar vídeo' : t('video.enable_video') || 'Ativar vídeo'}
        >
          {isVideoEnabled ? (
            <Video className="w-6 h-6 text-black" />
          ) : (
            <VideoOff className="w-6 h-6 text-white" />
          )}
        </button>
        <button
          onClick={toggleAudio}
          className={clsx(
            'p-2 rounded-full',
            isAudioEnabled ? 'bg-yellow-400' : 'bg-red-500'
          )}
          title={isAudioEnabled ? t('video.disable_audio') || 'Desativar áudio' : t('video.enable_audio') || 'Ativar áudio'}
        >
          {isAudioEnabled ? (
            <Mic className="w-6 h-6 text-black" />
          ) : (
            <MicOff className="w-6 h-6 text-white" />
          )}
        </button>
        <button
          onClick={toggleFullscreen}
          className="p-2 rounded-full bg-yellow-400"
          title={isFullscreen ? t('video.exit_fullscreen') || 'Sair da tela cheia' : t('video.fullscreen') || 'Tela cheia'}
        >
          {isFullscreen ? (
            <Minimize2 className="w-6 h-6 text-black" />
          ) : (
            <Maximize2 className="w-6 h-6 text-black" />
          )}
        </button>
      </div>
    </div>
  );
};
